import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from "chart.js";
import { SankeyController, Flow } from "chartjs-chart-sankey";
import { Chart } from "react-chartjs-2";
import api from "../utils/api";
import ChartBurgerMenu from "./ChartBurgerMenu";

ChartJS.register(
  SankeyController,
  Flow,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
);

const COLOR_PALETTE = [
  "#009f8d", // Teal Main
  "#ff9c07", // Orange Main
  "#1e88e5", // Blue
  "#8e24aa", // Purple
  "#0ea5e9", // Light Blue
  "#facc15", // Yellow
  "#00c6a9", // Light Teal
  "#ffb74d", // Light Orange
  "#6d28d9", // Deep Purple
  "#10b981", // Green
  "#f43f5e", // Rose Red
  "#059669", // Dark Green
];

const getNodeColor = (depth) =>
  COLOR_PALETTE[(depth - 1) % COLOR_PALETTE.length];

const parseEnergy = (val) => {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const num = parseFloat(val.replace(/[^\d.-]/g, ""));
    return isNaN(num) ? 0 : num;
  }
  return 0;
};

const SankeyDiagram = ({ username, dateRange, onLoadingChange }) => {
  const [hierarchyData, setHierarchyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [maxLevels, setMaxLevels] = useState(2);
  const chartRef = useRef(null);
  const treeDepthRef = useRef(2); // kept in sync with the computed treeDepth below

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFull = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);
      setIsFullScreen(isFull);
      if (isFull) {
        setMaxLevels(treeDepthRef.current); // show the client's full depth (generic)
      } else {
        setMaxLevels(2);
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    onLoadingChange?.(loading);
    return () => onLoadingChange?.(false);
  }, [loading, onLoadingChange]);


  useEffect(() => {
    const fetchData = async () => {
      if (!username || !dateRange?.start || !dateRange?.end) return;
      setLoading(true);
      setError(null);
      try {
        const formatDateTime = (d) => {
          if (!d) return "";
          const dateObj = typeof d === "string" ? new Date(d) : d;
          if (isNaN(dateObj.getTime())) return d;

          // Format as YYYY-MM-DD HH:mm:ss for backend compatibility
          const isoString = new Date(
            dateObj.getTime() - dateObj.getTimezoneOffset() * 60000,
          ).toISOString();
          return isoString.replace("T", " ").slice(0, 19);
        };
        const res = await api.get("/energy-hierarchy-tree", {
          params: {
            username,
            start: formatDateTime(dateRange.start),
            end: formatDateTime(dateRange.end),
          },
        });
        setHierarchyData(res.data);
      } catch (e) {
        console.error(e);
        setError("Failed to load energy flow data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [username, dateRange]);

  const { chartData, nodeStyles } = useMemo(() => {
    if (!hierarchyData) return { chartData: { datasets: [] }, nodeStyles: {} };

    const rawPoints = [];
    const nodeStyles = {};
    const columnMap = {}; // pins every node to its tree level (column)
    const processedNodes = new Set();

    // Recursively traverse tree
    const traverseNode = (node, depth = 1) => {
      // Use maxLevels state
      if (depth > maxLevels) return;

      const nodeId = node.machine_name; // Use machine_name directly

      if (processedNodes.has(nodeId)) return;
      processedNodes.add(nodeId);

      const nodeEnergy = parseEnergy(node.energy);

      // Register node style — use backend color if provided, fallback to palette for virtual/"Others" nodes
      nodeStyles[nodeId] = {
        label: `${node.machine_name} (${nodeEnergy.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kWh)`,
        color: node.color || getNodeColor(depth),
      };

      // Pin this node to its tree level so it never drifts into another column
      columnMap[nodeId] = depth - 1;

      // Create flows to children — keep zero-energy nodes too so they still show.
      // Skip children beyond the selected level (BEFORE pushing the flow) so the
      // level filter actually cuts deeper nodes instead of drawing them anyway.
      (node.children || []).forEach((child) => {
        const childId = child.machine_name;
        if (!childId) return;
        if (depth + 1 > maxLevels) return; // child is beyond the selected level

        const childEnergy = parseEnergy(child.energy);
        rawPoints.push({ from: nodeId, to: childId, flow: childEnergy });
        traverseNode(child, depth + 1);
      });
    };

    // Find root nodes based on hierarchy_path depth
    // Root nodes are those with the shortest hierarchy_path (usually depth 2 e.g., /root/)
    const nodes = Object.values(hierarchyData);
    const minPathLength = Math.min(
      ...nodes.map(
        (n) => (n.hierarchy_path || "").split("/").filter(Boolean).length,
      ),
    );
    const rootNodes = nodes.filter(
      (n) =>
        (n.hierarchy_path || "").split("/").filter(Boolean).length ===
        minPathLength,
    );

    rootNodes.forEach((root) => traverseNode(root));

    // Clamp tiny flows to a minimum visible thickness so they remain hoverable.
    // Keep realFlow for the tooltip so the user sees the true kWh.
    const maxFlow = rawPoints.reduce((m, p) => Math.max(m, p.flow), 0);
    const minVisible = maxFlow * 0.2; // 20% of largest flow — tiny flows large enough for label to fit
    // Zero-energy nodes get a thin visible band so they still appear (true kWh
    // stays 0 in the tooltip via realFlow).
    const zeroBand = maxFlow > 0 ? maxFlow * 0.04 : 1;
    const dataPoints = rawPoints.map((p) => ({
      from: p.from,
      to: p.to,
      flow: p.flow > 0 ? Math.max(p.flow, minVisible) : zeroBand,
      realFlow: p.flow,
    }));

    return {
      chartData: {
        datasets: [
          {
            label: "Energy Flow",
            data: dataPoints,
            colorFrom: (c) => nodeStyles[c?.raw?.from]?.color || "#94a3b8",
            colorTo: (c) => nodeStyles[c?.raw?.from]?.color || "#94a3b8",
            colorMode: "from",
            alpha: 0.6,
            hoverColorFrom: (c) => nodeStyles[c?.raw?.from]?.color || "#94a3b8",
            hoverColorTo: (c) => nodeStyles[c?.raw?.from]?.color || "#94a3b8",
            hoverColorMode: "from",
            hoverAlpha: 1,
            size: "max",
            borderWidth: 0,
            column: columnMap, // fix every node to its tree level (no drifting)
            labels: {
              formatter: (v) => nodeStyles[v]?.label || v,
              font: { size: isFullScreen ? 12 : 10, weight: "bold" },
              color: "#334155",
            },
            node: { width: isFullScreen ? 35 : 25, padding: isFullScreen ? 30 : 20 },
          },
        ],
      },
      nodeStyles,
    };
  }, [hierarchyData, isFullScreen, maxLevels]);

  // Actual depth of the tree — so the Levels dropdown only offers levels that exist.
  const treeDepth = useMemo(() => {
    if (!hierarchyData) return 2;
    const nodes = Object.values(hierarchyData);
    if (!nodes.length) return 2;
    const minPathLength = Math.min(
      ...nodes.map(
        (n) => (n.hierarchy_path || "").split("/").filter(Boolean).length,
      ),
    );
    const roots = nodes.filter(
      (n) =>
        (n.hierarchy_path || "").split("/").filter(Boolean).length ===
        minPathLength,
    );
    const getDepth = (node, d = 1) => {
      const kids = node.children || [];
      if (!kids.length) return d;
      return Math.max(...kids.map((c) => getDepth(c, d + 1)));
    };
    const maxD = roots.length
      ? Math.max(...roots.map((r) => getDepth(r, 1)))
      : 2;
    return Math.max(2, maxD);
  }, [hierarchyData]);

  // Keep the ref current so the fullscreen handler can read the latest depth.
  useEffect(() => {
    treeDepthRef.current = treeDepth;
  }, [treeDepth]);

  // Only show level options that actually exist (2 … treeDepth).
  const levelOptions = useMemo(() => {
    const opts = [];
    for (let l = 2; l <= treeDepth; l += 1) opts.push(l);
    return opts.length ? opts : [2];
  }, [treeDepth]);

  // Keep the selected level within the available range (except in fullscreen).
  useEffect(() => {
    if (!isFullScreen && maxLevels > treeDepth) {
      setMaxLevels(treeDepth);
    }
  }, [treeDepth, isFullScreen, maxLevels]);

  if (loading)
    return <div style={{ textAlign: "center", padding: 20 }}>Loading…</div>;
  if (error)
    return (
      <div style={{ textAlign: "center", padding: 20, color: "red" }}>
        {error}
      </div>
    );
  if (!hierarchyData) return null;

  const flowRows = chartData?.datasets?.[0]?.data || [];

  return (
    <div
      ref={chartRef}
      style={{
        background: "#fff",
        borderRadius: isFullScreen ? 0 : 16,
        padding: isFullScreen ? 40 : 20,
        minHeight: isFullScreen ? "100vh" : 500,
        width: "100%",
        height: isFullScreen ? "100vh" : "auto",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <h3 style={{ margin: 0 }}>Energy Flow Diagram</h3>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#f8fafc", padding: "4px 12px", borderRadius: "20px", border: "1px solid #e2e8f0" }}>
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>Levels:</span>
            <select 
              value={maxLevels} 
              onChange={(e) => setMaxLevels(Number(e.target.value))}
              style={{ border: "none", background: "transparent", fontSize: "12px", fontWeight: 700, color: "#0f172a", cursor: "pointer", outline: "none" }}
            >
              {levelOptions.map((l) => (
                <option key={l} value={l}>
                  {l === treeDepth ? `${l} (Full)` : l}
                </option>
              ))}
            </select>
          </div>
        </div>
        <ChartBurgerMenu
          chartRef={chartRef}
          chartTitle="Energy_Flow_Sankey"
          exportContainer={true}
          chartData={chartData.datasets[0].data.map((d) => [
            d.from,
            d.to,
            d.flow,
          ])}
          chartType="sankey"
        />
      </div>
      {flowRows.length > 0 ? (
        <div
          key={isFullScreen ? "full" : "dash"}
          style={{ flex: 1, minHeight: isFullScreen ? "80vh" : 250, width: "100%" }}
        >
          <Chart
            type="sankey"
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      if (ctx.raw?.flow !== undefined) {
                        const f = nodeStyles[ctx.raw.from]?.label || ctx.raw.from;
                        const t = nodeStyles[ctx.raw.to]?.label || ctx.raw.to;
                        const realKwh = ctx.raw.realFlow ?? ctx.raw.flow;
                        return `${f} \u2192 ${t}: ${realKwh.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kWh`;
                      }
                      return ctx.label || "";
                    },
                  },
                },
              },
            }}
          />
        </div>
      ) : (
        <div
          style={{
            height: 450,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            color: "#64748b",
          }}
        >
          This hierarchy does not have enough layers to build a flow diagram.
        </div>
      )}
    </div>
  );
};

export default SankeyDiagram;
